import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "@/components/Toast";
import { mergeStable } from "@/lib/mergeStable";
import { notifyPanicChanged, subscribePendingPanic, forcePendingPanicRefresh } from "@/lib/panicEvents";
import { EscalationChain } from "@/components/EscalationChain";
import { AlertOctagon, MapPin, Phone, Check, Square, CheckSquare } from "lucide-react";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";

// Ícono creado UNA sola vez a nivel de módulo (no en cada render/marker) —
// evita instanciar un L.divIcon nuevo por cada poll/marker.
const PENDING_DOT_ICON = L.divIcon({
  className: "",
  iconSize: [28, 28], iconAnchor: [14, 14],
  html: `<div style="width:28px;height:28px;background:#BF3437;border:3px solid #FFFBF4;border-radius:50%;box-shadow:0 0 0 1px #BF3437; animation: pulse-red 1.5s infinite"></div>`,
});

// A partir de esta cantidad de filas, la lista pasa a un contenedor con
// scroll interno de altura acotada (en vez de estirar la página entera)
// para mantenerla fluida con backlogs grandes.
const SCROLL_THRESHOLD = 12;
const MAX_LIST_HEIGHT = 640;

export default function AdminPanic() {
  const [list, setList] = useState(null);
  const [searchParams] = useSearchParams();
  // Prompt Y (2026-09-11) CAMBIO 5: al tocar la notificación push de un SOS,
  // se abre esta pantalla con ?id=<alerta> -- se ve en "Todas" (puede estar
  // ya atendida/resuelta) y se hace scroll + highlight hasta esa fila.
  const highlightId = searchParams.get("id");
  const [tab, setTab] = useState(() => (searchParams.get("id") ? "all" : "pendiente"));
  const [selected, setSelected] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = useCallback(async () => {
    // La pestaña "Pendientes" usa el poller ÚNICO y compartido con PanicBanner
    // (lib/panicEvents.js) — pedirle un refresh ahí en vez de disparar un
    // fetch propio evita el doble polling del mismo endpoint.
    if (tab === "pendiente") {
      await forcePendingPanicRefresh();
      return;
    }
    try {
      const { data } = await api.get(`/panic${tab === "all" ? "" : `?status=${tab}`}`);
      const rows = data || [];
      // Sólo actualiza el estado si algo realmente cambió (evita re-renders y
      // re-cálculos de mapa/lista en cada poll cuando el backlog está quieto).
      setList((prev) => {
        const { merged, changed } = mergeStable(prev, rows);
        return changed ? merged : prev;
      });
    } catch (e) { toast.error(formatApiError(e)); }
  }, [tab]);

  useEffect(() => {
    setSelected(new Set());
    setList(null); // vuelve al skeleton al cambiar de pestaña
    if (tab === "pendiente") {
      // Suscribe al poller compartido en vez de crear un intervalo propio —
      // esto es lo que evita el "loop" de pedidos contra /panic?status=pendiente
      // cuando esta página y el PanicBanner del layout están montados a la vez.
      const unsubscribe = subscribePendingPanic((rows) => {
        setList((prev) => {
          const { merged, changed } = mergeStable(prev, rows);
          return changed ? merged : prev;
        });
      });
      return unsubscribe;
    }
    load();
    const i = setInterval(load, 10000);
    return () => clearInterval(i);
  }, [tab, load]);

  const attend = useCallback(async (id) => {
    const note = window.prompt("Nota opcional (cómo estás respondiendo):") || "";
    try {
      await api.post(`/panic/${id}/attend`, { notes: note });
      toast.success("Te hiciste cargo del SOS");
      notifyPanicChanged();
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  }, [load]);

  const resolve = useCallback(async (id) => {
    const note = window.prompt("Notas de resolución:") || "";
    try {
      await api.post(`/panic/${id}/resolve`, { notes: note });
      toast.success("Alerta resuelta");
      notifyPanicChanged();
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  }, [load]);

  const toggleSelect = useCallback((id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const resolvableIds = useMemo(
    () => (list || []).filter((p) => p.status !== "resuelta").map((p) => p.id),
    [list]
  );

  const bulkResolve = useCallback(async (ids) => {
    if (ids.length === 0) return;
    const note = window.prompt(`Notas de resolución para ${ids.length} alerta(s):`) || "";
    setBulkBusy(true);
    try {
      const { data } = await api.post("/panic/bulk-resolve", { ids, notes: note });
      toast.success(`${data.resolved} resuelta(s)${data.skipped ? ` · ${data.skipped} sin permiso` : ""}`);
      notifyPanicChanged();
      setSelected(new Set());
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBulkBusy(false); }
  }, [load]);

  const pending = useMemo(() => (list ? list.filter((p) => p.status === "pendiente") : []), [list]);
  const pendingWithLocation = useMemo(() => pending.filter((p) => p.latitude), [pending]);
  const firstLat = pendingWithLocation[0]?.latitude;
  const firstLng = pendingWithLocation[0]?.longitude;
  // sólo recalcula si cambia el primer punto — MapContainer usa este center
  // únicamente en el montaje inicial, no recrea el mapa en polls siguientes
  const mapCenter = useMemo(() => [firstLat || -34.6, firstLng || -58.4], [firstLat, firstLng]);

  useEffect(() => {
    if (!highlightId || !list) return;
    const el = document.querySelector(`[data-testid="panic-${highlightId}"]`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [highlightId, list]);

  return (
    <div style={{ padding: "20px 24px 60px" }}>
      <div style={{ fontSize: 11, letterSpacing: "0.2em", color: "#BF3437", fontWeight: 700, textTransform: "uppercase" }}>Emergencias</div>
      <h1 style={{ fontSize: 32, fontWeight: 900, margin: "4px 0 4px", color: "#0A0A0A", letterSpacing: "-0.02em" }}>Alertas de pánico</h1>
      <p style={{ color: "var(--invict-text-muted)", fontSize: 13, margin: "0 0 14px" }}>El SOS se rutea al supervisor responsable del objetivo. "Atender" significa que te hacés cargo de este SOS (queda tu nombre y la hora).</p>

      <EscalationChain />

      <div style={{ display: "flex", gap: 4, marginBottom: 14, borderBottom: "1px solid var(--invict-bone-3)", alignItems: "center", flexWrap: "wrap" }}>
        {[
          { k: "pendiente", label: `Pendientes${pending.length ? ` (${pending.length})` : ""}` },
          { k: "en_atencion", label: "En atención" },
          { k: "resuelta", label: "Resueltas" },
          { k: "all", label: "Todas" },
        ].map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)}
            style={{ padding: "10px 16px", border: "none", background: "transparent", color: tab === t.k ? "#0A0A0A" : "var(--invict-text-muted)", fontWeight: 700, fontSize: 12, letterSpacing: "0.05em", textTransform: "uppercase", cursor: "pointer", borderBottom: tab === t.k ? "3px solid #BF3437" : "3px solid transparent" }}
            data-testid={`tab-${t.k}`}
          >{t.label}</button>
        ))}
        <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          {selected.size > 0 && (
            <button onClick={() => bulkResolve(Array.from(selected))} disabled={bulkBusy}
              className="btn-primary" style={{ padding: "8px 14px", fontSize: 11 }}
              data-testid="panic-resolve-selected">
              Resolver seleccionadas ({selected.size})
            </button>
          )}
          {resolvableIds.length > 0 && (
            <button onClick={() => bulkResolve(resolvableIds)} disabled={bulkBusy}
              className="btn-ghost" style={{ padding: "8px 14px", fontSize: 11 }}
              data-testid="panic-resolve-all">
              Resolver todas ({resolvableIds.length})
            </button>
          )}
        </div>
      </div>

      {pendingWithLocation.length > 0 && (
        <div className="card" style={{ padding: 0, overflow: "hidden", height: 320, marginBottom: 16 }}>
          <MapContainer center={mapCenter} zoom={14} style={{ height: "100%", width: "100%" }}>
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            {pendingWithLocation.map((p) => (
              <Marker key={p.id} position={[p.latitude, p.longitude]} icon={PENDING_DOT_ICON}>
                <Popup><b>{p.user_name}</b><br/>{new Date(p.created_at).toLocaleString("es-AR")}</Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>
      )}

      <div className="card" style={{ padding: 0, overflow: "hidden" }}>
        {list == null && <div style={{ padding: 20 }}><div className="skel" style={{ height: 60 }} /></div>}
        {list && list.length === 0 && <div style={{ padding: 40, textAlign: "center", color: "var(--invict-text-muted)" }}>Sin alertas en este filtro.</div>}
        {list && list.length > 0 && (
          <div style={list.length > SCROLL_THRESHOLD ? { maxHeight: MAX_LIST_HEIGHT, overflowY: "auto" } : undefined}>
            {list.map((p) => (
              <PanicRow key={p.id} p={p} selected={selected} onToggleSelect={toggleSelect} onAttend={attend} onResolve={resolve} highlight={p.id === highlightId} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/** Fila memoizada: mientras mergeStable() mantenga la misma referencia del
 * objeto `p` entre polls, React.memo evita re-renderizar filas que no
 * cambiaron (aunque el array `list` completo sea uno nuevo en cada poll). */
const PanicRow = memo(function PanicRow({ p, selected, onToggleSelect, onAttend, onResolve, highlight }) {
  const isSelected = selected.has(p.id);
  return (
    <div className={p.status === "pendiente" ? "fade-in" : ""}
      style={{ padding: "16px 20px", borderBottom: "1px solid var(--invict-bone-3)", borderLeft: p.status === "pendiente" ? "4px solid #BF3437" : "none", display: "grid", gridTemplateColumns: "auto auto 2fr 2fr 1fr auto", gap: 14, alignItems: "center", background: highlight ? "rgba(191,52,55,0.08)" : undefined, boxShadow: highlight ? "inset 0 0 0 2px #BF3437" : undefined }}
      data-testid={`panic-${p.id}`}>
      {p.status !== "resuelta" ? (
        <button onClick={() => onToggleSelect(p.id)} data-testid={`panic-select-${p.id}`}
          style={{ background: "none", border: "none", cursor: "pointer", color: isSelected ? "#BF3437" : "var(--invict-text-muted)", padding: 0 }}>
          {isSelected ? <CheckSquare size={18} /> : <Square size={18} />}
        </button>
      ) : <span />}
      <AlertOctagon size={22} style={{ color: p.status === "pendiente" ? "#BF3437" : "var(--invict-text-muted)" }} />
      <div style={{ overflow: "hidden" }}>
        <div style={{ fontSize: 14, fontWeight: 800, color: "#0A0A0A", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{p.user_name}</div>
        <div style={{ fontSize: 10, color: "var(--invict-text-muted)", textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 700 }}>{p.user_role}{p.user_phone ? ` · ${p.user_phone}` : ""}</div>
        {p.client_name && <div style={{ fontSize: 11, color: "var(--invict-text-muted)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>En objetivo: <b>{p.client_name}</b></div>}
        {p.routed_supervisor_name && <div style={{ fontSize: 11, color: "var(--invict-text-muted)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>Responsable SOS: {p.routed_supervisor_name}</div>}
      </div>
      <div style={{ fontSize: 11, color: "var(--invict-text-muted)", overflow: "hidden" }}>
        <div>{new Date(p.created_at).toLocaleString("es-AR")}</div>
        {p.latitude && <div><MapPin size={10} style={{ display: "inline", marginRight: 3 }}/><a href={`https://www.google.com/maps?q=${p.latitude},${p.longitude}`} target="_blank" rel="noreferrer" style={{ color: "#BF3437" }}>{p.latitude.toFixed(4)}, {p.longitude.toFixed(4)}</a></div>}
        {p.attended_by_name && <div>Se hizo cargo: <b>{p.attended_by_name}</b></div>}
        {p.resolution_notes && <div style={{ fontStyle: "italic", marginTop: 3, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>"{p.resolution_notes}"</div>}
      </div>
      <span className={`badge ${p.status === "pendiente" ? "red" : p.status === "en_atencion" ? "amber" : "green"}`}>{p.status === "pendiente" ? "Pendiente" : p.status === "en_atencion" ? "En atención" : "Resuelta"}</span>
      <div style={{ display: "flex", gap: 6 }}>
        {p.user_phone && <a href={`tel:${p.user_phone.replace(/[^\d+]/g, "")}`} className="btn-ghost" style={{ padding: "6px 10px", fontSize: 10 }} data-testid={`panic-call-${p.id}`}><Phone size={11}/></a>}
        {p.sos_contact_phone && <a href={`tel:${p.sos_contact_phone.replace(/[^\d+]/g, "")}`} className="btn-ghost" style={{ padding: "6px 10px", fontSize: 10 }} title="Llamar al contacto SOS configurado" data-testid={`panic-call-contact-${p.id}`}><Phone size={11} style={{ color: "#BF3437" }}/></a>}
        {p.status === "pendiente" && <button onClick={() => onAttend(p.id)} className="btn-primary" style={{ padding: "6px 10px", fontSize: 10 }} title="Atender = me hago cargo de este SOS" data-testid={`panic-attend-${p.id}`}>Atender</button>}
        {p.status !== "resuelta" && <button onClick={() => onResolve(p.id)} className="btn-ghost" style={{ padding: "6px 10px", fontSize: 10, color: "#16a34a", borderColor: "#16a34a" }} data-testid={`panic-resolve-${p.id}`}><Check size={11}/></button>}
      </div>
    </div>
  );
});
